import { Cart } from "@dining/api-client";
import { AppProps, StateSlice } from "./app";

export type CartProps = {
  cart: {
    value?: Cart;
    loading?: boolean;
    error?: Error;
  };
};

export type CartState = CartProps & {
  cart: {
    setLoading: (loading: boolean) => void;
    setCart: (value?: Cart) => void;
    setError: (error?: Error) => void;
    getCart: () => Promise<void>;
    deleteItemFromCart: (cafeId: string, itemId: string) => Promise<void>;
    abandonCart: () => Promise<void>;
    checkoutCart: (cafeId: string) => Promise<void>;
  };
};

export const defaultCartProps: CartProps = {
  cart: {
    loading: false,
  },
};

export const createCartState: (props?: Partial<AppProps>) => StateSlice<CartState> =
  (props) => (set, get) => ({
    cart: {
      ...defaultCartProps.cart,
      ...props?.cart,
      setLoading: (loading: boolean) =>
        set(
          (state) => ({
            ...state,
            cart: {
              ...state.cart,
              loading,
            },
          }),
          false,
          {
            type: "cart.setLoading",
            loading,
          },
        ),
      deleteItemFromCart: async (cafeId: string, itemId: string) => {
        const {
          appInsights,
          client,
          cart: { setLoading, setCart },
        } = get();
        try {
          setLoading(true);
          const cart = await client?.removeItemFromCafeCart(cafeId, itemId);
          setCart(cart);
        } catch (error) {
          appInsights.trackException(
            {
              exception: error as Error,
            },
            {
              method: "deleteItemFromCart",
              cafeId,
              itemId,
            },
          );
        } finally {
          setLoading(false);
        }
      },
      getCart: async () => {
        const {
          client,
          cart: { setLoading, setCart, setError },
        } = get();
        try {
          if (client) {
            setLoading(true);
            const cart = await client.getCart();
            setCart(cart.items.length > 0 ? cart : undefined);
          } else {
            setError(new Error("No client available"));
          }
        } catch (error) {
          setError(error as Error);
        } finally {
          setLoading(false);
        }
      },
      setCart: (value?: Cart) =>
        set(
          (state) => ({
            ...state,
            cart: {
              ...state.cart,
              value,
            },
          }),
          false,
          {
            type: "cart.setCart",
            value,
          },
        ),
      setError: (error?: Error) =>
        set(
          (state) => ({
            ...state,
            cart: {
              ...state.cart,
              error,
            },
          }),
          false,
          {
            type: "cart.setError",
            error,
          },
        ),
      abandonCart: async () => {
        const {
          client,
          cart: { value, setCart, setError },
        } = get();
        try {
          if (value && client) {
            await client.abandonCart();
            setCart(undefined);
          }
        } catch (error) {
          setError(error as Error);
        }
      },
      checkoutCart: async (cafeId: string) => {
        const {
          client,
          cart: { value, setCart, setError },
        } = get();
        try {
          if (value && value.items.length > 0 && client) {
            const checkedOutCart = await client.checkoutCafeCart(cafeId);
            setCart(checkedOutCart);
          }
        } catch (error) {
          setError(error as Error);
        }
      },
    },
  });
